import detailMessage from './detailMessage';
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { completeGoogleOAuth } from "./api";
import ConfirmShell from "./components/ConfirmShell";
import { useLang } from "./LangContext";

type Status = "loading" | "ok" | "error";

interface OAuthData {
  account_email?: string;
  missing_scopes?: string[];
}

interface PageState {
  status: Status;
  data?: OAuthData;
  message?: string;
}

export default function GoogleOAuthCallbackPage() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const [state, setState] = useState<PageState>({ status: "loading" });

  useEffect(() => {
    const code = params.get("code");
    const stateToken = params.get("state");
    const error = params.get("error");
    if (error) {
      setState({ status: "error", message: `Google sign-in was cancelled (${error}).` });
      return;
    }
    if (!code || !stateToken) {
      setState({ status: "error", message: t.somethingWentWrong });
      return;
    }
    let cancelled = false;
    completeGoogleOAuth(code, stateToken)
      .then((data: OAuthData) => {
        if (cancelled) return;
        setState({ status: "ok", data });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const detail = detailMessage(err, t.somethingWentWrong);
        setState({ status: "error", message: detail });
      });
    return () => {
      cancelled = true;
    };
  }, [params]);

  return (
    <ConfirmShell>
      {state.status === "loading" && (
        <>
          <div className="confirm-spinner" aria-hidden="true" />
          <h2>{t.connectingGoogleCalendar}</h2>
        </>
      )}
      {state.status === "ok" && (state.data!.missing_scopes || []).length === 0 && (
        <>
          <div className="confirm-status-icon ok" aria-hidden="true">✓</div>
          <h2>{t.googleCalendarConnected}</h2>
          <p>
            {state.data!.account_email
              ? t.googleCalendarSyncMessage(state.data!.account_email)
              : t.googleCalendarSyncMessageNoEmail}
          </p>
          <p className="close-tab-hint">
            <Link to="/">{t.backToDashboard}</Link>
          </p>
        </>
      )}
      {state.status === "ok" && (state.data!.missing_scopes || []).length > 0 && (
        <>
          <div className="confirm-status-icon error" aria-hidden="true">!</div>
          <h2>{t.googlePermissionsIncomplete}</h2>
          <p>{t.googlePermissionsIncompleteMessage}</p>
          <p>{t.googlePermissionsRetryMessage}</p>
          <p className="close-tab-hint"><Link to="/">{t.backToDashboard}</Link></p>
        </>
      )}
      {state.status === "error" && (
        <>
          <div className="confirm-status-icon error" aria-hidden="true">!</div>
          <h2>{t.googleCalendarError}</h2>
          <p>{state.message}</p>
          <p className="close-tab-hint">
            <Link to="/">{t.backToDashboard}</Link>
          </p>
        </>
      )}
    </ConfirmShell>
  );
}
